import React from "react";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Message } from "primereact/message";
import { ProgressSpinner } from "primereact/progressspinner";
import { Tag } from "primereact/tag";

import { ScreenHealthView, UiDifference } from "../../../types/eroundProvisioning";
import { TagSeverity, unreachableReason } from "../../../utils/eroundProvisioning";
import { formatInstant } from "../../../utils/instantFormat";
import { useUiHealth } from "./useUiHealth";

const STATUS: Record<string, { label: string; severity: TagSeverity }> = {
  match: { label: "OK", severity: "success" },
  notice: { label: "Changed (harmless)", severity: "info" },
  drift: { label: "Changed – runs stop", severity: "danger" },
  unreachable: { label: "Not checked", severity: "warning" },
};

const SOURCE: Record<string, string> = { nightly: "Nightly check", run: "Provisioning run" };

// PrimeReact's Tag has no neutral severity; an unknown status is shown verbatim in the surface colour.
const NEUTRAL = { background: "var(--surface-300)", color: "var(--text-color)" };

const StatusTag: React.FC<{ status: string }> = ({ status }) => {
  const known = STATUS[status];
  return known ? <Tag value={known.label} severity={known.severity} /> : <Tag value={status} style={NEUTRAL} />;
};

const formatValue = (value: unknown): string => {
  if (value === null || value === undefined) return "—";
  if (typeof value === "string") return value;
  if (Array.isArray(value)) return value.map(formatValue).join(", ");
  return typeof value === "object" ? JSON.stringify(value) : String(value);
};

const breakingFirst = (a: UiDifference, b: UiDifference) => Number(b.severity === "breaking") - Number(a.severity === "breaking");

// One line per difference, breaking first, with what eRound shows instead when the worker found it.
const changes = (screen: ScreenHealthView) =>
  screen.differences.length
    ? [...screen.differences].sort(breakingFirst).map((d) => (
        <div key={`${d.element}.${d.attribute}`} className="mb-1">
          <span>{`${d.element} · ${d.attribute}: ${formatValue(d.old)} → ${formatValue(d.new)}`}</span>
          {d.candidates.length > 0 && <small className="block text-600">{`now: ${d.candidates.join(", ")}`}</small>}
        </div>
      ))
    : screen.status === "unreachable"
      ? unreachableReason(screen.reason)
      : "—";

/** Each eRound screen the automation uses, as last checked (SOPS-164). */
const ScreenHealthPanel: React.FC = () => {
  const health = useUiHealth();

  if (health === undefined) return <ProgressSpinner style={{ width: "2rem", height: "2rem" }} strokeWidth="4" />;
  if (health === null) {
    return <Message severity="error" className="w-full" text="Screen health could not be loaded. Please try again." />;
  }
  if (!health.checked) return <p className="mt-0">No eRound screen check has run yet.</p>;

  return (
    <div>
      <p className="mt-0 text-600">
        Last check {formatInstant(health.lastCheckedAt)} ·{" "}
        {SOURCE[health.lastSource ?? ""] ?? health.lastSource ?? "—"}
        {health.guardMode === "warn" && <span className="block mt-1 font-semibold">Guard in warn mode: runs are not stopped</span>}
      </p>
      <DataTable value={health.screens} dataKey="screen">
        <Column header="Screen" field="title" />
        <Column header="Status" body={(s: ScreenHealthView) => <StatusTag status={s.status} />} />
        <Column header="Since" body={(s: ScreenHealthView) => formatInstant(s.since)} />
        <Column header="Last checked" body={(s: ScreenHealthView) => formatInstant(s.lastCheckedAt)} />
        <Column header="Changes" body={changes} />
      </DataTable>
    </div>
  );
};

export default ScreenHealthPanel;
